import { ButtonIcon, ButtonSize, ButtonVariant, Icon, IconSize, IconVariants, Image, Input, Shape, TextArea } from "@next-platform/client-common-spicy-ui";
import { useTranslation } from "@next-platform/client-common-core";
import { Accordion, AccordionItem } from "@nextui-org/react";

import type { IAdvancedOptionsProps } from "@/components/AvatarGeneratorModal/types";
import { Col, Row, SpicyButton } from "@/components/customComponents";
import Trans from "@/components/Trans/Trans";

const NEGATIVE_PROMPT_ACCORDION_CLASSES = {
  trigger: "py-0 bg-transparent justify-start cursor-pointer font-semibold",
  title: "py-0 mb-0 text-[16px] text-gray-12 tracking-tighter",
  base: "py-0 m-0",
  indicator: "py-0 ml-1 text-xs mb-0",
} as const;

const REFERENCE_IMAGE_ACCORDION_CLASSES = {
  trigger: "py-0 bg-transparent cursor-pointer font-semibold",
  title: "text-[16px] text-gray-12 tracking-tighter",
  base: "p-0 m-0",
} as const;

const SEED_ACCORDION_CLASSES = REFERENCE_IMAGE_ACCORDION_CLASSES;

// eslint-disable-next-line max-lines-per-function -- Presentational component; three independent accordions don't warrant further splitting.
export const AdvancedOptions = ({
  supportsNegativePrompt,
  supportsReferenceImage,
  negativePrompt,
  guidanceImage,
  fixedSeed,
  seedLocked,
  generatedImagesCount,
  fileInputRef,
  onNegativePromptChange,
  onRemoveGuidanceImage,
  onImageUploadClick,
  onImageUpload,
  onSeedLockToggle,
}: IAdvancedOptionsProps) => {
  const { t } = useTranslation();
  return (
    <Col className="gap-2">
      {supportsNegativePrompt && (
        <Accordion className="p-0 space-y-0 gap-0">
          <AccordionItem
            HeadingComponent="div"
            classNames={NEGATIVE_PROMPT_ACCORDION_CLASSES}
            title={t("common:modal.generateAvatar.negativePrompt.label")}
            className="p-0 m-0 border-b-0"
          >
            <Col align="center">
              <TextArea
                value={negativePrompt}
                placeholder={t("common:modal.generateAvatar.negativePrompt.helperText")}
                aria-labelledby="negative-prompt"
                rows={2}
                maxRows={5}
                onChange={onNegativePromptChange}
                className="w-full"
              />
              <p className="p-0 ml-[2px] leading-[16px] font-normal text-[12px] dark text-gray-11 w-full text-left">
                <Trans i18nKey="common:modal.generateAvatar.negativePrompt.uploadHelperText" />
              </p>
            </Col>
          </AccordionItem>
        </Accordion>
      )}

      {!!supportsReferenceImage && (
        <Accordion className="p-0 space-y-0 gap-0">
          <AccordionItem
            HeadingComponent="div"
            classNames={REFERENCE_IMAGE_ACCORDION_CLASSES}
            title={t("common:modal.generateAvatar.referenceImage.label")}
            className="p-0 m-0 border-b-0"
          >
            <>
              <p className="p-0 ml-[2px] mb-2 leading-[16px] font-normal text-[12px] dark text-gray-11 w-full text-left">
                <Trans i18nKey="common:modal.generateAvatar.referenceImage.uploadHelperText" />
              </p>
              <Row className="w-full" justify="center">
                {guidanceImage ? (
                  <div className="relative">
                    <Image
                      src={guidanceImage as string}
                      alt={t("common:modal.generateAvatar.imageAlt.guidanceImage")}
                      width={200}
                      height={200}
                      className="object-cover rounded-md"
                    />
                    <ButtonIcon
                      icon={IconVariants.CLOSE}
                      size={ButtonSize.SMALL}
                      shape={Shape.SQUARE}
                      variant={ButtonVariant.TRANSPARENT}
                      onClick={onRemoveGuidanceImage}
                      className="absolute top-2 right-2 bg-gray-9 hover:bg-gray-10"
                      aria-label={t("common:modal.generateAvatar.ariaLabel.removeImage")}
                    />
                  </div>
                ) : (
                  <SpicyButton variant="secondary" onClick={onImageUploadClick} startContent={<Icon icon={IconVariants.UPLOAD} />}>
                    <Trans i18nKey="common:modal.generateAvatar.referenceImage.cta.uploadImage" />
                  </SpicyButton>
                )}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={onImageUpload} className="hidden" />
              </Row>
            </>
          </AccordionItem>
        </Accordion>
      )}

      {generatedImagesCount > 0 && (
        <Accordion className="p-0 space-y-0 gap-0">
          <AccordionItem
            HeadingComponent="div"
            classNames={SEED_ACCORDION_CLASSES}
            title={t("common:modal.generateAvatar.seed.label")}
            className="p-0 m-0 border-b-0"
          >
            <Col align="center">
              <Row align="center" className="w-full gap-2">
                <Input value={fixedSeed} readOnly className="flex-1" aria-label={t("common:modal.generateAvatar.seed.label")} />
                <SpicyButton
                  isIconOnly
                  startContent={<Icon icon={seedLocked ? IconVariants.LOCK : IconVariants.UNLOCK} size={IconSize.MEDIUM} />}
                  variant="tertiary_small_short"
                  onPress={onSeedLockToggle}
                  aria-label={
                    seedLocked ? t("common:modal.generateAvatar.ariaLabel.seedLocked") : t("common:modal.generateAvatar.ariaLabel.seedUnlocked")
                  }
                />
              </Row>
              <p className="p-0 ml-[2px] leading-[16px] font-normal text-[12px] dark text-gray-11 w-full text-left">
                <Trans i18nKey="common:modal.generateAvatar.seed.helperText" />
              </p>
            </Col>
          </AccordionItem>
        </Accordion>
      )}
    </Col>
  );
};
